import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowUp, FaRobot } from "react-icons/fa";

function FloatingOrbs() {
  const orbs = [
    { size: 300, x: "10%", y: "20%", color: "#22c55e", delay: 0 },
    { size: 200, x: "80%", y: "10%", color: "#3b82f6", delay: 1 },
    { size: 250, x: "60%", y: "70%", color: "#22c55e", delay: 2 },
    { size: 150, x: "20%", y: "80%", color: "#3b82f6", delay: 0.5 },
  ];
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {orbs.map((orb, i) => (
        <motion.div key={i} className="absolute rounded-full"
          style={{ width: orb.size, height: orb.size, left: orb.x, top: orb.y,
            background: `radial-gradient(circle, ${orb.color}08, transparent 70%)`, filter: "blur(40px)" }}
          animate={{ x: [0, 30, -20, 0], y: [0, -30, 20, 0], scale: [1, 1.1, 0.95, 1] }}
          transition={{ duration: 12 + i * 3, repeat: Infinity, ease: "easeInOut", delay: orb.delay }} />
      ))}
    </div>
  );
}

function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.5, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          whileHover={{ scale: 1.15, boxShadow: "0 0 30px rgba(34,197,94,0.5)" }}
          whileTap={{ scale: 0.9 }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full flex items-center justify-center text-white shadow-2xl"
          style={{ background: "linear-gradient(135deg, #22c55e, #3b82f6)" }}>
          <FaArrowUp className="text-base" />
        </motion.button>
      )}
    </AnimatePresence>
  );
}

function LoadingScreen({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 2400);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center"
      style={{ background: "#050a14" }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
      {/* Outer spin ring */}
      <div className="relative mb-8">
        <motion.div className="w-24 h-24 rounded-full"
          style={{ border: "2px solid transparent", borderTopColor: "#22c55e", borderRightColor: "#3b82f6" }}
          animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
        {/* Inner counter-spin ring */}
        <motion.div className="absolute inset-3 rounded-full"
          style={{ width: "72px", height: "72px", border: "2px solid transparent", borderBottomColor: "#22c55e", borderLeftColor: "#3b82f6" }}
          animate={{ rotate: -360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }} />
        {/* Center icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <FaRobot className="text-xl" style={{ color: "#22c55e" }} />
          </motion.div>
        </div>
      </div>

      {/* Brand name */}
      <motion.div className="font-orbitron text-2xl font-black text-white mb-2 tracking-widest"
        animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 1.5, repeat: Infinity }}>
        UEA
      </motion.div>

      <motion.p className="font-mono-code text-xs text-slate-400 tracking-widest uppercase mb-6"
        animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity }}>
        Initializing Trading Engine...
      </motion.p>

      {/* Progress bar */}
      <div className="w-48 h-0.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
        <motion.div className="h-full rounded-full"
          style={{ background: "linear-gradient(90deg, #22c55e, #3b82f6)" }}
          initial={{ width: "0%" }} animate={{ width: "100%" }}
          transition={{ duration: 2.2, ease: "easeInOut" }} />
      </div>
    </motion.div>
  );
}

export default function Layout({ children }) {
  const [loading, setLoading] = useState(true);
  return (
    <>
      <AnimatePresence>
        {loading && <LoadingScreen onDone={() => setLoading(false)} />}
      </AnimatePresence>
      {!loading && (
        <motion.div className="relative min-h-screen"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
          <FloatingOrbs />
          <div className="relative z-10">{children}</div>
          <ScrollToTop />
        </motion.div>
      )}
    </>
  );
}
